import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Clock, CheckCircle2, ChevronRight, Zap } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { EmptyState, Spinner, ProgressBar, useToast } from "@/components/earnos/kit";
import { colors } from "@/lib/theme";

const DIFF_COLOR = { Easy: colors.success, Medium: colors.warning, Hard: colors.error };

export default function Moves() {
  const navigate = useNavigate();
  const toast = useToast();
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch("/moves");
      setMoves(d.moves);
    } catch (e) {
      toast.show(e?.message || "Failed to load", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const done = moves.filter((m) => m.status === "done").length;

  if (loading) return <Spinner className="py-24" />;

  return (
    <div className="animate-fade-in max-w-3xl">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="moves-back" className="text-ink lg:hidden"><ChevronLeft size={26} /></button>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink">Money Moves</h1>
      </div>
      <p className="text-base text-ink-2 mt-1">One clear action at a time. No streaks, no guilt.</p>

      {moves.length === 0 ? (
        <EmptyState icon={Zap} title="No Money Moves yet" message="Create your Income Engine and your daily Money Moves will appear here." actionTitle="Go to Engine" onAction={() => navigate("/engine")} />
      ) : (
        <>
          <div className="mt-5">
            <ProgressBar value={done / moves.length} />
            <p className="text-sm text-ink-2 mt-2">{done} of {moves.length} completed</p>
          </div>
          <div className="space-y-3 mt-5">
            {moves.map((m) => {
              const doneState = m.status === "done";
              const skipped = m.status === "skipped";
              return (
                <button key={m.move_id} onClick={() => navigate(`/move/${m.move_id}`)} data-testid={`move-${m.day}`} className={`w-full flex items-center gap-3 rounded-xl border border-line bg-surface-2 p-4 hover:border-line-strong transition-colors text-left ${doneState || skipped ? "opacity-60" : ""}`}>
                  <div className="w-10 h-10 rounded-xl bg-brand/20 flex items-center justify-center text-brand-light text-base font-extrabold shrink-0">{m.day}</div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-base font-bold text-ink ${doneState ? "line-through" : ""}`}>{m.title}</p>
                    <div className="flex items-center gap-1.5 mt-1 text-sm text-ink-2">
                      <Clock size={13} /><span>{m.estimated_time}</span>
                      <span className="w-1.5 h-1.5 rounded-full ml-1" style={{ backgroundColor: DIFF_COLOR[m.difficulty] || colors.onSurfaceSecondary }} />
                      <span>{m.difficulty}</span>
                    </div>
                  </div>
                  {doneState ? <CheckCircle2 size={24} className="text-ok" /> : skipped ? <span className="text-sm text-ink-2 font-semibold">Skipped</span> : <ChevronRight size={20} className="text-ink-2" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
